import { DOCUMENT_TYPE_DISPLAY_NAMES } from '@/modules/documents/documents.constants';
import { isClientProfileDocumentType } from '@/modules/documents/documents.utils';
import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  DocumentType,
  PresentationBundleKind,
  PresentationDocumentType,
  PresentationPageKind,
} from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';

/**
 * Represents which clients to include in the document summary bundle.
 * Maps to the `includeFirstGrantor` and `includeSecondGrantor` booleans
 * on DocumentSummaryTemplateBundleConfiguration.
 */
export enum ClientSelection {
  ClientOne = 'CLIENT_ONE',
  ClientTwo = 'CLIENT_TWO',
  AllClients = 'ALL_CLIENTS',
}

export const CLIENT_SELECTION_OPTIONS = [
  { display: 'Client one', value: ClientSelection.ClientOne },
  { display: 'Client two', value: ClientSelection.ClientTwo },
  { display: 'All clients', value: ClientSelection.AllClients },
] as const;

export interface DocumentSummaryBundleConfigFormShape {
  documentType: PresentationDocumentType | '';
  clientSelection: ClientSelection | '';
}

export function getDefaultDocumentSummaryValues(
  existingBundle?: PresentationTemplateEditorBundle
): DocumentSummaryBundleConfigFormShape {
  if (!existingBundle?.documentSummaryTemplateBundleConfiguration) {
    return {
      documentType: '',
      clientSelection: '',
    };
  }

  const config = existingBundle.documentSummaryTemplateBundleConfiguration;

  return {
    documentType: config.documentType ?? '',
    clientSelection: clientBoolsToSelection(
      config.includeFirstGrantor,
      config.includeSecondGrantor
    ),
  };
}

function clientBoolsToSelection(
  includeFirst: boolean,
  includeSecond: boolean
): ClientSelection | '' {
  if (includeFirst && includeSecond) return ClientSelection.AllClients;
  if (includeFirst) return ClientSelection.ClientOne;
  if (includeSecond) return ClientSelection.ClientTwo;
  return '';
}

function clientSelectionToBools(selection: ClientSelection | ''): {
  includeFirstGrantor: boolean;
  includeSecondGrantor: boolean;
} {
  switch (selection) {
    case ClientSelection.ClientOne:
      return { includeFirstGrantor: true, includeSecondGrantor: false };
    case ClientSelection.ClientTwo:
      return { includeFirstGrantor: false, includeSecondGrantor: true };
    case ClientSelection.AllClients:
      return { includeFirstGrantor: true, includeSecondGrantor: true };
    default:
      return { includeFirstGrantor: false, includeSecondGrantor: false };
  }
}

const CLIENT_DISPLAY_NAMES: Record<ClientSelection, string> =
  Object.fromEntries(
    CLIENT_SELECTION_OPTIONS.map((opt) => [opt.value, opt.display])
  ) as Record<ClientSelection, string>;

export interface DocumentSummaryBundleConfig {
  documentType?: PresentationDocumentType | null;
  includeFirstGrantor: boolean;
  includeSecondGrantor: boolean;
}

/**
 * Returns true if the document summary bundle config is missing required
 * fields. Document type is always required; client selection is only
 * required for client-profile document types.
 */
export function isDocumentSummaryConfigIncomplete(
  config: DocumentSummaryBundleConfig | null | undefined
): boolean {
  if (!config) return true;
  if (!config.documentType) return true;
  // isClientProfileDocumentType accepts `string`, and both enums share
  // the same underlying string values
  if (isClientProfileDocumentType(config.documentType)) {
    return !config.includeFirstGrantor && !config.includeSecondGrantor;
  }
  return false;
}

/**
 * Returns a descriptive label for a configured document summary bundle.
 * For client-profile document types: "Will - Client one".
 * For other document types: just the document type name, e.g. "Gift tax return".
 * Returns null when the config is missing, so callers can fall back to a
 * generic label.
 */
export function getDocumentSummaryBundleLabel(
  config: DocumentSummaryBundleConfig | null | undefined
): string | null {
  if (!config?.documentType) return null;

  // The enum values are identical strings between DocumentType and
  // PresentationDocumentType
  const docTypeDisplay =
    DOCUMENT_TYPE_DISPLAY_NAMES[
      config.documentType as unknown as DocumentType
    ] ?? config.documentType;

  if (isClientProfileDocumentType(config.documentType)) {
    const clientSelection = clientBoolsToSelection(
      config.includeFirstGrantor,
      config.includeSecondGrantor
    );
    if (!clientSelection) return null;
    return `${docTypeDisplay} - ${CLIENT_DISPLAY_NAMES[clientSelection]}`;
  }

  return docTypeDisplay;
}

export function buildDocumentSummaryBundleFromForm(
  formValues: DocumentSummaryBundleConfigFormShape,
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  const { includeFirstGrantor, includeSecondGrantor } = clientSelectionToBools(
    formValues.clientSelection
  );

  const bundlePages: PresentationTemplateEditorPage[] = [
    {
      id: getNewPageId(PresentationPageKind.DocumentSummaryPage),
      kind: PresentationPageKind.DocumentSummaryPage,
    },
  ];

  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.DocumentSummaryBundle),
    kind: PresentationBundleKind.DocumentSummaryBundle,
    documentSummaryTemplateBundleConfiguration: {
      __typename: 'DocumentSummaryTemplateBundleConfiguration' as const,
      id: '',
      documentType: formValues.documentType || null,
      includeFirstGrantor,
      includeSecondGrantor,
    },
    pages: bundlePages,
  };
}
